<template>
  <Dialog
  :stateId="dialogId"
  maxWidth="960px"
  @dialog-closing="close"
  @close="close">
    <v-card v-if="show" id="bid-duplicate-dialog">
      <v-card-title class="primary white--text">
        <span>{{ bid ? $t('copyBidFrom', { name: bid.name }) : $t('copyBid') }}</span>
        <template v-if="bid">
          <span class="ml-4 subtitle-1 d-flex align-center">
            <v-icon class="mr-1" color="white">{{ IconHelpers.getIconForObjectType('account') }}</v-icon>{{ accountName(bid.supplierAccountId) }}
          </span>
          <span class="ml-4 subtitle-1 d-flex align-center">
            <v-icon class="mr-1" color="white">{{ IconHelpers.getIconForObjectType('destination') }}</v-icon>{{ accountName(bid.destinationAccountId) }}
          </span>
        </template>
        <v-spacer/>
        <BaseDialogActions
        hideRefresh
        hideFullscreen
        customCancel
        elementId="bid-duplicate-actions"
        @close="close"/>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row v-if="!bid" class="my-12" justify="center" align="center">
          <v-progress-circular indeterminate color="primary"/>
        </v-row>
        <FormWrapper
        v-else
        formRef="duplicateForm"
        :buttonText="$t('copyBid')"
        :disabled="!copyable || saving"
        @submit="duplicate">
          <DetailCard :fillHeight="false" elementClass="mb-3">
            <v-row dense class="mt-n3">
              <v-col cols="12">
                <v-text-field
                v-model="name"
                :label="$t('bidName')"
                color="black"
                :rules="[rules.required]"
                :counter="(name || '').length >= nameMaxLength ? nameMaxLength : false"
                :maxlength="nameMaxLength"/>
              </v-col>
              <v-col cols="12" sm="5">
                <AccountAutocomplete
                :onlyInternal="true"
                hideRefresh
                :title="$t('destination')"
                :accountId="destinationAccountId"
                :originSpot="bid.spot"
                @account-chosen="onDestinationChosen">
                  <template #item="{item}">
                    <span
                    v-if="item.routeDistanceFromOrigin !== undefined"
                    class="ml-auto pl-2 caption grey--text text--darken-1 text-no-wrap d-flex align-center">
                      <Icon
                      icon="mdi-car"
                      iconColor="grey darken-1"
                      :tooltipText="$t('approximateRouteDistance')"/>
                      {{ $t('approxMilesShort', { distance: item.routeDistanceFromOrigin.toFixed(1) }) }}
                    </span>
                    <span v-else-if="originSpotValid && item.distanceFromOrigin === undefined" class="ml-auto pl-2 d-flex align-center">
                      <Icon
                      icon="mdi-map-marker-off"
                      iconColor="grey darken-1"
                      :tooltipText="$t('receiverLocationNotSet')"/>
                    </span>
                  </template>
                </AccountAutocomplete>
              </v-col>
              <v-col cols="6" sm="2">
                <v-text-field
                v-model="distance"
                :label="$t('distance')"
                color="black"
                :rules="[rules.positiveIntegerBetweenOrBlank(0, MAX_BID_DISTANCE)]"/>
              </v-col>
              <v-col cols="12" sm="5">
                <AccountAutocomplete
                :onlyExternal="true"
                required
                hideRefresh
                userSetting="supplierAccountAutocomplete"
                :title="$t('supplier')"
                :accountId="supplierAccountId"
                :prohibitedSet="prohibitedSupplierIds"
                @account-chosen="supplierAccountId = $event ? $event.accountId : undefined"/>
              </v-col>
            </v-row>
          </DetailCard>
          <div v-if="skippedProductCount > 0" class="caption text--secondary pb-3">
            {{ $t('bidProductsNotCarried', { skipped: skippedProductCount, total: bid.products.length, destination: destinationName }) }}
          </div>
          <BidDetail :bid="previewBid" filled class="mb-4"/>
          <template #left-action>
            <v-btn text @click="close">{{ $t('cancel') }}</v-btn>
          </template>
        </FormWrapper>
      </v-card-text>
    </v-card>
  </Dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { BidProductStatus, RfbStatus } from '@/utils/Enumerations.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { routeMilesFromOrigin } from '@/utils/map/RouteDistance.js'
import IconHelpers from '@/utils/IconHelpers'
import { MAX_BID_DISTANCE } from '@/model/Bid.js'
import { StringConstants } from '@/utils/constants'

export default {
  name: 'BidDuplicateDialog',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    BidDetail: () => import('@/components/bid/BidDetail.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('bid-duplicate-dialog'),
    rules: fieldRules.rules,
    IconHelpers,
    MAX_BID_DISTANCE,
    nameMaxLength: StringConstants.ExtraLongStringLength,
    show: false,
    openRequestId: 0,
    bid: null,
    siblingBids: [],
    name: undefined,
    destinationAccountId: undefined,
    distance: undefined,
    supplierAccountId: undefined,
    carriedProductIds: undefined,
    saving: false
  }),

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tract', ['allTimberCategories', 'allConversions', 'allLandownerTypes']),
    ...mapGetters('product', { storeProducts: 'allProducts' }),

    distanceEmpty () {
      return this.distance === null || this.distance === undefined || this.distance === ''
    },

    originSpotValid () {
      return typeof this.bid.spot?.latitude === 'number' && typeof this.bid.spot?.longitude === 'number'
    },

    copyDistance () {
      return parseInt(this.distance) || 0
    },

    prohibitedSupplierIds () {
      return new Set(this.siblingBids
        .filter(b => b.destinationAccountId === this.destinationAccountId && b.supplierAccountId != null)
        .map(b => b.supplierAccountId))
    },

    copiedProducts () {
      if (this.carriedProductIds === undefined) return this.bid.products
      return this.bid.products.filter(p => this.carriedProductIds.includes(p.productId))
    },

    skippedProductCount () {
      return this.bid.products.length - this.copiedProducts.length
    },

    destinationName () {
      return this.accountName(this.destinationAccountId)
    },

    copyable () {
      return !!this.name?.trim() &&
        !!this.destinationAccountId &&
        !!this.supplierAccountId &&
        !this.prohibitedSupplierIds.has(this.supplierAccountId) &&
        this.carriedProductIds !== undefined &&
        this.rules.positiveIntegerBetweenOrBlank(0, MAX_BID_DISTANCE)(this.distance) === true
    },

    previewBid () {
      return {
        ...this.bid,
        destinationAccountId: this.destinationAccountId,
        supplierAccountId: this.supplierAccountId,
        distance: this.copyDistance,
        rfbStatus: RfbStatus.Submitted.value,
        outcome: null,
        reason: null,
        products: this.copiedProducts.map(p => ({ ...p, status: BidProductStatus.None.value }))
      }
    }
  },

  methods: {
    ...mapActions('bid', ['fetchBid', 'createBid', 'createBidProduct']),
    ...mapActions('account', ['fetchAccountById']),
    ...mapActions('tract', ['fetchTimberCategories', 'fetchConversions', 'fetchLandownerTypes']),
    ...mapActions('product', ['fetchProducts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async open (bid, siblingBids) {
      const requestId = ++this.openRequestId
      this.bid = null
      this.siblingBids = siblingBids
      this.name = undefined
      this.destinationAccountId = undefined
      this.distance = undefined
      this.supplierAccountId = undefined
      this.carriedProductIds = undefined
      this.show = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '960px' })
      if (!this.storeProducts.length) this.fetchProducts({ includeByProducts: false, includeProduction: true })
      if (!this.allTimberCategories.length) this.fetchTimberCategories()
      if (!this.allConversions.length) this.fetchConversions()
      if (!this.allLandownerTypes.length) this.fetchLandownerTypes()
      const full = await this.fetchBid(bid.bidId)
      if (requestId !== this.openRequestId || !this.show) return
      this.name = full.name
      this.destinationAccountId = full.destinationAccountId
      this.distance = full.distance
      this.bid = full
      this.loadCarriedProducts(full.destinationAccountId)
    },

    async loadCarriedProducts (accountId) {
      this.carriedProductIds = undefined
      const account = await this.fetchAccountById(accountId)
      if (accountId !== this.destinationAccountId) return
      this.carriedProductIds = (account.accountProducts ?? [])
        .filter(ap => !ap.isScalable)
        .map(ap => ap.productId)
    },

    async onDestinationChosen (account) {
      const previousId = this.destinationAccountId
      this.destinationAccountId = account ? account.accountId : undefined
      if (!account) return

      const destinationChanged = account.accountId !== previousId
      if (destinationChanged) this.loadCarriedProducts(account.accountId)
      if (!destinationChanged && !this.distanceEmpty) return
      if (destinationChanged) this.distance = undefined

      const [miles] = await routeMilesFromOrigin(this.bid.spot, [{ latitude: account.receiverSpotLatitude, longitude: account.receiverSpotLongitude }])
      if (this.destinationAccountId !== account.accountId || !this.distanceEmpty) return

      const rounded = Math.round(miles)
      if (miles !== undefined && rounded <= MAX_BID_DISTANCE) this.distance = String(rounded)
    },

    async duplicate () {
      if (!this.copyable || this.saving) return
      this.saving = true
      let created
      try {
        const source = this.bid
        created = await this.createBid({
          tractId: source.tractId,
          name: this.name.trim(),
          spot: {
            latitude: source.spot.latitude,
            longitude: source.spot.longitude,
            countrySubdivision: source.spot.countrySubdivision,
            countrySecondarySubdivision: source.spot.countrySecondarySubdivision
          },
          destinationAccountId: this.destinationAccountId,
          distance: this.copyDistance,
          supplierAccountId: this.supplierAccountId,
          bidDate: source.bidDate,
          bidExpiration: source.bidExpiration,
          harvestStartDate: source.harvestStartDate,
          harvestEndDate: source.harvestEndDate,
          timberCategoryId: source.timberCategoryId,
          conversionId: source.conversionId,
          landownerTypeId: source.landownerTypeId
        })
        await Promise.all(this.copiedProducts.map(product => this.copyProduct(created.bidId, product)))
        this.setSnack(this.$t('bidCopied'))
      } finally {
        this.saving = false
        if (created) {
          this.$emit('saved')
          this.close()
        }
      }
    },

    async copyProduct (bidId, product) {
      await this.createBidProduct({
        bidId,
        payload: {
          productId: product.productId,
          harvestVolume: product.harvestVolume,
          status: BidProductStatus.None.value,
          deliveredRate: product.deliveredRate,
          costComponents: product.costComponents.map(component => ({ bidCostTypeId: component.bidCostTypeId, rate: component.rate }))
        }
      })
    },

    accountName (accountId) {
      return this.allAccounts.find(a => a.accountId === accountId)?.name
    },

    close () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.show = false
    }
  }
}
</script>
